import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
  useLayoutEffect
} from 'react'
import PropTypes from 'prop-types'
import {
  classNames,
  useUpdateRef,
  Icon,
  iconProps,
  useSetState
} from '@zambezi/sdk/common'
import Lookup from './Lookup'
import { inputGhostRenderer, suggestionTextRenderer } from './renderers'
import { scrollToSelectedItem, getUnpackedSuggestions } from './utils'

/**
 * Create a React Select component
 *
 * @template T
 * @template Item
 * @param {import('./interfaces').ComboBoxProps<T, Item>} props
 * @param {React.Ref<import('./interfaces').LookupRef>} ref
 * @returns {JSX.Element}
 */
function comboBox (props, ref) {
  const {
    className,
    getSuggestionValue,
    renderInput,
    withGrouping,
    getGroupChildren,
    renderGroupLabel,
    renderSuggestion,
    selectedIconProps,
    suggestionClassName,
    suggestionProps,
    iconProps,
    disabledItemsIndices,
    enableVirtualScroll,
    noAutoSuggestion
  } = props
  const unpackedSuggestions = useMemo(
    () =>
      getUnpackedSuggestions(
        props.suggestions,
        withGrouping,
        getGroupChildren,
        renderGroupLabel
      ),
    [props.suggestions, withGrouping, getGroupChildren, renderGroupLabel]
  )
  /**
   * @typedef {{suggestions: Item[] | {group: Item[], label: React.ReactNode}[], disabledIndices: number[]}} State
   * @type {[State, React.Dispatch<State>]} */
  const [{ suggestions, disabledIndices }, setState] = useSetState({
    suggestions: unpackedSuggestions,
    disabledIndices: disabledItemsIndices
  })

  const prevStateSuggestions = useRef(suggestions)
  const elementRef = useRef()
  const inputRef = useRef()

  /** @type {(props: {value: string, reason: string}) => void} */
  const handleSuggestionsFetchRequested = useCallback(
    ({ value }) => {
      const {
        suggestionMatches = (/** @type {Item} */ s, /** @type {string} */ value) => {
          const suggestionValue = getSuggestionValue(s).toLowerCase()
          return suggestionValue.indexOf(value.trim().toLowerCase()) !== -1
        }
      } = props

      const selectedSuggestion = withGrouping
        ? unpackedSuggestions.reduce((s, { group }) => {
          return s || findSelectedSuggestion(group, value, getSuggestionValue)
        }, null)
        : findSelectedSuggestion(unpackedSuggestions, value, getSuggestionValue)

      let newSuggestions = unpackedSuggestions
      let newDisabledIndices = disabledItemsIndices
      if (value.trim().length && !selectedSuggestion) {
        const { suggestions, disabledIndices } = filterSuggestions(
          unpackedSuggestions,
          disabledItemsIndices,
          isSuggestionMatching,
          withGrouping
        )
        newSuggestions = suggestions
        newDisabledIndices = disabledIndices
      }

      setState({
        suggestions: newSuggestions,
        disabledIndices: newDisabledIndices
      })
      function isSuggestionMatching (suggestion) {
        return suggestionMatches(suggestion, value)
      }
    },
    [
      getSuggestionValue,
      withGrouping,
      props.suggestionMatches,
      unpackedSuggestions,
      disabledItemsIndices
    ]
  )

  const handleSuggestionsClearRequested = useCallback(() => {
    setState({ suggestions: [] })
  }, [])

  const shouldRenderSuggestions = useCallback(
    () => !!props.suggestions.length,
    [props.suggestions]
  )

  /** @type {(suggestion: Item, query: import('./interfaces').SuggestionQuery<Item>, index: number) => string} */
  const _suggestionClassName = useCallback(
    (suggestion, { query, getSuggestionValue }, index) =>
      classNames(
        {
          'zb-select-dropdown-list-item-is-selected': isSuggestionSelected(
            suggestion,
            query,
            getSuggestionValue
          )
        },
        suggestionClassName(suggestion, props, index)
      ),
    [suggestionClassName]
  )
  /** @type {(suggestion: Item, props: import('./interfaces').SuggestionQuery<Item>, index: number) => React.HTMLAttributes<HTMLElement>} */
  const _suggestionProps = useCallback(
    (suggestion, { query, getSuggestionValue }, index) => ({
      'aria-selected': isSuggestionSelected(
        suggestion,
        query,
        getSuggestionValue
      ),
      ...suggestionProps(suggestion, props, index)
    }),
    [suggestionProps]
  )

  /** @type {(suggestion: Item, query: import('./interfaces').SuggestionQuery<Item>, index: number) => JSX.Element} */
  const renderComboBoxSuggestion = useCallback(
    (suggestion, { query, getSuggestionValue }) => {
      const isSelected = isSuggestionSelected(
        suggestion,
        query,
        getSuggestionValue
      )

      return (
        <div>
          {renderSuggestion(suggestion, { query, getSuggestionValue })}

          {isSelected && (
            <Icon
              name='core-dropdown-item-selected'
              title='Selected Item'
              aria-hidden='true'
              iconProps={selectedIconProps}
            />
          )}
        </div>
      )
    },
    [renderSuggestion]
  )

  /** @type {import('./interfaces').InputRenderer<T, Item>} */
  const renderComboBoxInput = useCallback(
    props => {
      const { ref, isFocused, isOpen, isDisabled } = props
      const iconName = isFocused
        ? 'core-combobox-expanded'
        : 'core-combobox-collapsed'

      const inputProps = {
        ...props,
        ref: el => {
          if (typeof ref === 'function') {
            ref(el)
          } else if (ref && 'current' in ref) {
            ref.current = el
          }
          inputRef.current = el
        },
        noAutoSuggestion
      }

      return (
        <span>
          {renderInput(inputProps)}

          <Icon
            name={iconName}
            onClick={isDisabled ? null : handleIconClick}
            aria-hidden='true'
            title=''
            iconProps={iconProps}
          />
        </span>
      )

      function handleIconClick () {
        const element = elementRef.current
        const input = inputRef.current
        isOpen ? element.close() : element.open()
        input && ((input.element || input)).focus()
      }
    },
    [renderInput]
  )

  let res
  if (suggestions.length === 1) {
    res = suggestions.length + ' result available use arrow keys to navigate'
  } else if (suggestions.length === 0) {
    res = 'no matches found'
  } else {
    res = suggestions.length + ' results available use arrow keys to navigate'
  }

  useEffect(() => {
    setState({ disabledIndices: disabledItemsIndices })
  }, [disabledItemsIndices])
  useLayoutEffect(() => {
    const isShown = suggestions.length > 0
    const wasShown = prevStateSuggestions.current.length > 0
    const didOpen = isShown === true && wasShown === false
    prevStateSuggestions.current = suggestions
    if (didOpen) {
      scrollToSelectedItem(elementRef.current.element || elementRef.current)
    }
  }, [suggestions])
  useUpdateRef(ref, elementRef)

  return (
  <>
    <Lookup
      {...props}
      ref={elementRef}
      disabledItemsIndices={disabledIndices}
      getGroupChildren={groupChildrenGetter}
      renderGroupLabel={groupLabelRenderer}
      suggestions={suggestions}
      className={classNames('zb-combobox', className)}
      suggestionsFetchRequested={handleSuggestionsFetchRequested}
      suggestionsClearRequested={handleSuggestionsClearRequested}
      renderInput={renderComboBoxInput}
      renderSuggestion={renderComboBoxSuggestion}
      suggestionClassName={_suggestionClassName}
      suggestionProps={_suggestionProps}
      shouldRenderSuggestions={shouldRenderSuggestions}
      enableVirtualScroll={enableVirtualScroll}
    />
    <div aria-live='polite' aria-description={res} />
  </>
  )
}

function findSelectedSuggestion (suggestions, value, getSuggestionValue) {
  for (let i = 0; i < suggestions.length; i++) {
    if (isSuggestionSelected(suggestions[i], value, getSuggestionValue)) {
      return suggestions[i]
    }
  }
  return false
}

/**
 * @template Item
 * @param {Item} suggestion
 * @param {import('./interfaces').SuggestionQuery<Item>} query
 * @param {import('./interfaces').SuggestionValueGetter<Item>} getSuggestionValue
 */
function isSuggestionSelected (suggestion, query, getSuggestionValue) {
  return (
    String(query).toLowerCase() === getSuggestionValue(suggestion).toLowerCase()
  )
}

/**
 * @template Item
 * @param {{ group: Item[], label: React.ReactNode}} d
 */
function groupChildrenGetter (d) {
  return d.group
}

/**
 * @template Item
 * @param {{ group: Item[], label: React.ReactNode}} d
 */
function groupLabelRenderer (d) {
  return d.label
}

/**
 * @template Item
 * @param {{ group: Item[], label: React.ReactNode}[]|Item[]} items
 * @param {number[]} disabledItemsIndices
 * @param {(item: Item, index: number) => boolean} predicate
 * @param {boolean} withGrouping
 */
function filterSuggestions (
  items,
  disabledItemsIndices,
  predicate,
  withGrouping
) {
  return withGrouping
    ? filterGroups(items, disabledItemsIndices, predicate)
    : filterItems(items, disabledItemsIndices, predicate, 0)
}

/**
 * @template Item
 * @param {Item[]} items
 * @param {number[]} disabledItemsIndices
 * @param {(item: Item, index: number) => boolean} predicate
 * @param {number} offset
 */
function filterItems (items, disabledItemsIndices, predicate, offset) {
  const suggestions = []
  const disabledIndices = []
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (predicate(item)) {
      if (disabledItemsIndices.indexOf(i + offset) !== -1) {
        disabledIndices.push(suggestions.length + offset)
      }
      suggestions.push(item)
    }
  }
  return { suggestions, disabledIndices }
}

/**
 * @template Item
 * @param {{ group: Item[], label: React.ReactNode}[]} groups
 * @param {number[]} disabledItemsIndices
 * @param {(item: Item, index: number) => boolean} predicate
 * @param {number} offset
 */
function filterGroups (groups, disabledItemsIndices, predicate) {
  const suggestions = []
  const disabledIndices = []
  let offset = 0
  let groupOffset = 0
  for (let i = 0; i < groups.length; i++) {
    const { group, label } = groups[i]
    const {
      suggestions: groupSuggestions,
      disabledIndices: groupDisabledIndices
    } = filterItems(group, disabledItemsIndices, predicate, offset)
    if (groupSuggestions.length) {
      suggestions.push({ group: groupSuggestions, label })
      disabledIndices.push(
        ...groupDisabledIndices.map(index => index + groupOffset - offset)
      )
    }
    offset += group.length
    groupOffset += groupSuggestions.length
  }
  return { suggestions, disabledIndices }
}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').ComboBoxProps<T, Item> & React.RefAttributes<import('./interfaces').LookupRef>>}
 */
const ComboBox = forwardRef(comboBox)

ComboBox.displayName = 'ComboBox'

function noop () {}

ComboBox.propTypes = {
  suggestions: PropTypes.array,
  getSuggestionValue: PropTypes.func,
  defaultValue: PropTypes.string,
  renderInput: PropTypes.func,
  suggestionMatches: PropTypes.func,
  renderSuggestion: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  withGrouping: PropTypes.bool,
  renderGroupLabel: PropTypes.func,
  getGroupChildren: PropTypes.func,
  onGroupLabelClick: PropTypes.func,
  suggestionClassName: PropTypes.func,
  suggestionProps: PropTypes.func,
  selectedIconProps: iconProps,
  iconProps,
  disabledItemsIndices: PropTypes.arrayOf(PropTypes.number),
  enableVirtualScroll: PropTypes.bool,
  noAutoSuggestion: PropTypes.bool
}

ComboBox.defaultProps = {
  suggestions: [],
  disabledItemsIndices: [],
  getSuggestionValue: String,
  renderInput: inputGhostRenderer,
  renderSuggestion: suggestionTextRenderer,
  defaultValue: '',
  onSuggestionSelected: noop,
  onChange: noop,
  onBlur: noop,
  onFocus: noop,
  suggestionProps: noop,
  suggestionClassName: noop,
  withGrouping: false,
  renderGroupLabel: String,
  getGroupChildren: () => [],
  onGroupLabelClick: noop,
  enableVirtualScroll: false,
  noAutoSuggestion: false
}

export default ComboBox
